/******/ (() => { // webpackBootstrap
/* eslint-disable */

class GlobalDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeElements = this.querySelectorAll('[data-drawer-close]');
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.closeAllDrawers = this.closeAllDrawers.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-drawer'});
    window.eventBus.on('close:all:drawers', this.closeAllDrawers);
    this.closeElements.forEach((element) => {
      element.addEventListener('click', this.closeDrawer);
    });

    if (window.Shopify.designMode) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  openDrawer() {
    window.eventBus.emit('close:all:drawers', this);
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('js-drawer-open');
    this.addEventListener('transitionend', this.handleTransitionEnd);
    window.eventBus.emit('drawer:opened', this);
  }

  closeDrawer() {
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('js-drawer-open');
    this.removeEventListener('transitionend', this.handleTransitionEnd);
    window.eventBus.emit('drawer:closed', this);
  }

  closeAllDrawers(e) {
    if (e === this) return;
    this.closeDrawer();
  }

  handleTransitionEnd(e) {
    if (e.target === this) {
      this.trapFocus();
    }
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    if (!firstFocusableElement) return;
    firstFocusableElement.focus();
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  handleResize() {
    this.classList.add('disable-transitions');
    setTimeout(() => {
      this.classList.remove('disable-transitions');
    }, 500);
  }

  disconnectedCallback() {
    if (window.Shopify.designMode) {
      window.removeEventListener('resize', this.handleResize);
    }
  }
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));
}

/* ===== Minimal money formatter (mirrors theme.js formatMoney, scoped to this component) ===== */
function formatMoney(cents, format) {
  const moneyFormat = format || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const parts = value.split('.');
  const withThousands = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formattedValue = `${withThousands}.${parts[1]}`;

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = moneyFormat.match(placeholderRegex);
  if (!match) return moneyFormat;

  switch (match[1]) {
    case 'amount_no_decimals':
      return moneyFormat.replace(placeholderRegex, withThousands);
    default:
      return moneyFormat.replace(placeholderRegex, formattedValue);
  }
}

class ProductCustomizeDrawer extends GlobalDrawer {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id');
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
    this.moneyWithCurrencyFormat = this.getAttribute('data-money-with-currency-format') || this.moneyFormat;
    this.body = this.querySelector('[data-customize-drawer-body]');
    this.totalDisplay = this.querySelector('[data-customize-drawer-total]');
    this.summaryList = this.querySelector('[data-customize-drawer-summary]');
    this.errorDisplay = this.querySelector('[data-customize-drawer-error]');
    this.applyButton = this.querySelector('[data-customize-drawer-apply]');
    this.clearButton = this.querySelector('[data-customize-drawer-clear]');
    this.options = Array.from(this.querySelectorAll('[data-customize-option]'));
    this.appliedOptions = [];

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleCardInteraction = this.handleCardInteraction.bind(this);
    this.recomputeTotal = this.recomputeTotal.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleVariantUpdated = this.handleVariantUpdated.bind(this);
    this.handleMainFormSubmit = this.handleMainFormSubmit.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.wetheme.webcomponentRegistry.register({key: 'component-product-customize-drawer'});

    document.addEventListener('click', this.handleTriggerClick);
    // Capture phase: runs before product-form's own submit handler on the form
    document.addEventListener('submit', this.handleMainFormSubmit, true);
    this.addEventListener('click', this.handleCardInteraction);
    this.options.forEach((option) => {
      option.addEventListener('change', this.recomputeTotal);
    });
    this.applyButton?.addEventListener('click', this.handleApply);
    this.clearButton?.addEventListener('click', this.handleClear);
    // Variant changes re-render the Customize Upgrades button, so re-apply its label
    window.eventBus.on('variant:updated', this.handleVariantUpdated);

    this.recomputeTotal();
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-open-product-customize-drawer]');
    if (!trigger) return;
    if (trigger.getAttribute('data-drawer-target') !== this.id) return;
    event.preventDefault();
    this.openDrawer();
  }

  handleCardInteraction(event) {
    const toggle = event.target.closest('[data-customize-info-toggle]');
    if (!toggle) return;
    event.preventDefault();
    event.stopPropagation();
    const inner = toggle.closest('.customize-drawer__option-inner');
    if (inner) inner.classList.toggle('is-flipped');
  }

  handleVariantUpdated(data) {
    if (data?.sectionId && data.sectionId !== this.sectionId) return;
    this.updateTriggers();
  }

  closeDrawer() {
    // Closing without applying discards unsaved changes to the selection
    if (!this.isApplying) this.restoreAppliedSelection();
    super.closeDrawer();
  }

  restoreAppliedSelection() {
    this.options.forEach((option) => {
      option.checked = this.appliedOptions.includes(option);
    });
    this.clearError();
    this.recomputeTotal();
  }

  getSelectedOptions() {
    return this.options.filter((option) => option.checked && !option.disabled);
  }

  getTotalCents(options) {
    return options.reduce((sum, option) => sum + (parseInt(option.dataset.variantPrice, 10) || 0), 0);
  }

  recomputeTotal() {
    const selected = this.getSelectedOptions();
    if (this.totalDisplay) {
      this.totalDisplay.textContent = formatMoney(this.getTotalCents(selected), this.moneyFormat);
    }
    if (this.clearButton) {
      this.clearButton.hidden = selected.length === 0 && this.appliedOptions.length === 0;
    }
    this.renderSummary(selected);
  }

  renderSummary(selected) {
    if (!this.summaryList) return;

    if (selected.length === 0) {
      this.summaryList.innerHTML = '<p class="customize-drawer__summary-empty">No upgrades selected yet</p>';
      return;
    }

    this.summaryList.innerHTML = selected
      .map((option) => {
        const price = formatMoney(parseInt(option.dataset.variantPrice, 10) || 0, this.moneyFormat);
        return `<div class="customize-drawer__summary-row"><span>${escapeHtml(option.dataset.name)}</span><span>${price}</span></div>`;
      })
      .join('');
  }

  showError(message) {
    if (!this.errorDisplay) return;
    this.errorDisplay.textContent = message;
    this.errorDisplay.classList.add('is-visible');
  }

  clearError() {
    if (!this.errorDisplay) return;
    this.errorDisplay.textContent = '';
    this.errorDisplay.classList.remove('is-visible');
  }

  handleApply() {
    this.clearError();
    const selectedOptions = this.getSelectedOptions();

    const missingAddon = selectedOptions.find((option) => !option.dataset.variantId);
    if (missingAddon) {
      this.showError(`"${missingAddon.dataset.name}" is not available for purchase right now.`);
      return;
    }

    this.appliedOptions = selectedOptions;
    this.updateTriggers();

    this.isApplying = true;
    this.closeDrawer();
    this.isApplying = false;
  }

  handleClear() {
    this.options.forEach((option) => {
      option.checked = false;
    });
    this.recomputeTotal();
    this.handleApply();
  }

  getGroupLabel(group) {
    return group.charAt(0).toUpperCase() + group.slice(1);
  }

  updateTriggers() {
    const groups = [...new Set(this.appliedOptions.map((option) => this.getGroupLabel(option.dataset.optionGroup)))];
    const groupText = groups.length > 1
      ? `${groups.slice(0, -1).join(', ')} & ${groups[groups.length - 1]}`
      : groups[0];
    const total = formatMoney(this.getTotalCents(this.appliedOptions), this.moneyWithCurrencyFormat);

    document.querySelectorAll(`[data-open-product-customize-drawer][data-drawer-target="${this.id}"]`).forEach((trigger) => {
      if (!trigger.dataset.defaultLabel) trigger.dataset.defaultLabel = trigger.textContent.trim();
      const label = groups.length > 0 ? `${groupText} Customization +${total}` : trigger.dataset.defaultLabel;
      trigger.textContent = label;
      trigger.setAttribute('aria-label', label);
      trigger.classList.toggle('has-upgrades', groups.length > 0);
    });
  }

  getMainForm() {
    return document.querySelector(`product-form[data-section-id="${this.sectionId}"] form`);
  }

  /* ===== Main Add to Cart with upgrades applied =====
     Takes over the product form submit so the ring and its upgrades are added in ONE
     /cart/add request (all or nothing), reusing product-form's own validation,
     loading state and post-add behaviour (open cart drawer / added message / redirect). */
  async handleMainFormSubmit(event) {
    const form = event.target;
    if (this.appliedOptions.length === 0 || form !== this.getMainForm()) return;

    const productForm = form.closest('product-form');
    if (!productForm || typeof productForm.processResponse !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    if (productForm.isProcessing) return;

    productForm.cacheSubmissionElements();
    if (!productForm.checkRequiredInputs()) return;
    productForm.disableBlankInputs();
    productForm.showLoadingState();
    productForm.isProcessing = true;

    try {
      const items = this.buildCartItems(form, productForm.quantity);
      if (productForm.giftWrapEnabled()) {
        items.push({ id: parseInt(productForm.giftWrapProductId, 10), quantity: productForm.quantity });
      }

      const payload = { items };
      if (productForm.cartType === 'drawer') payload.sections = 'cart-drawer';

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const responseJson = await response.json();

      if (response.ok) {
        if (productForm.cartType === 'drawer') productForm.updateCartDrawer(responseJson);
      } else {
        console.error(`Error adding ring with upgrades to cart: ${responseJson.description || responseJson.message || response.statusText}`);
      }
      productForm.processResponse(response);
    } catch (error) {
      console.error(`Error adding ring with upgrades to cart: ${error.description || error.message}`);
      productForm.hideLoadingState();
      productForm.enableBlankInputs();
    } finally {
      productForm.isProcessing = false;
    }
  }

  /* ===== Ring line item (from the product form) followed by one line per applied upgrade ===== */
  buildCartItems(form, quantity) {
    const bundleId = `customize-${this.sectionId}-${Date.now()}`;
    const ring = { quantity, properties: {} };

    new FormData(form).forEach((value, key) => {
      if (key === 'id') ring.id = parseInt(value, 10);
      else if (key === 'selling_plan' && value) ring.selling_plan = parseInt(value, 10);
      else {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') ring.properties[match[1]] = value;
      }
    });

    this.appliedOptions.forEach((option) => {
      const label = this.getGroupLabel(option.dataset.optionGroup);
      ring.properties[label] = ring.properties[label] ? `${ring.properties[label]}, ${option.dataset.name}` : option.dataset.name;
    });
    ring.properties._customize_bundle = bundleId;

    const upgradeFor = form.closest('product-details')?.dataset.url || '';
    const upgrades = this.appliedOptions.map((option) => ({
      id: parseInt(option.dataset.variantId, 10),
      quantity,
      properties: {
        _customize_bundle: bundleId,
        _upgrade_for: upgradeFor,
      },
    }));

    return [ring, ...upgrades];
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('submit', this.handleMainFormSubmit, true);
    window.eventBus.off?.('variant:updated', this.handleVariantUpdated);
    super.disconnectedCallback();
  }
}

if (!customElements.get('product-customize-drawer')) {
  customElements.define('product-customize-drawer', ProductCustomizeDrawer);
}

/******/ })()
;
